import React, { useContext, useEffect, useMemo, useState } from "react"
import { ShadowView, textStyles } from "../style/style"
import { Appearance, Text, TouchableOpacity, StyleSheet, View } from "react-native"
import { ThemeContext } from "./context/ThemeContext"
import { Storage } from "./pwa"
import darkTheme from "../style/dark-theme"
import lightTheme from "../style/light-theme"
import { Ionicons, MaterialIcons } from "@expo/vector-icons"
import Analytics from "./analytics"

const ThemeSelector = () => {
  const { theme, setTheme } = useContext(ThemeContext)
  const { borderColor, primary, bgSecond, white, textMain } = theme.colors

  const [useSystemAppearance, setUseSystemAppearance] = useState(false)

  const selectedTheme = useMemo(() => {
    if (useSystemAppearance) {
      return 'system'
    } else {
      return theme.name
    }
  }, [useSystemAppearance, theme.name])

  useEffect(() => {
    const initSystemAppearance = async () => {
      const stored = await Storage.getItem('ecountabl-theme')
      setUseSystemAppearance(!stored || stored === 'system')
    }
    initSystemAppearance()
  }, [])

  const handleThemeChange = async (value: 'system' | 'light' | 'dark') => {
    await Storage.setItem('ecountabl-theme', value)
    if (value === 'system') {
      setUseSystemAppearance(true)
      const colorScheme = Appearance.getColorScheme()
      setTheme(colorScheme === 'dark' ? darkTheme : lightTheme)
    } else {
      setUseSystemAppearance(false)
      setTheme(value === 'dark' ? darkTheme : lightTheme)
    }

    // Analytics: log a normalized theme choice
    Analytics.track('theme_changed', { theme: value === 'system' ? (Appearance.getColorScheme() === 'dark' ? 'dark' : 'light') : value })
  }

  return (
    <ShadowView style={{borderRadius: 8}}>
      <View style={{flex: 1, borderRadius: 8, overflow: "hidden", borderWidth: 1, borderColor, flexDirection: "row", alignItems: "center"}}>
        <TouchableOpacity
          onPress={() => handleThemeChange("light")}
          disabled={selectedTheme === "light"}
          style={[
            styles.themeButton,
            {
              backgroundColor: selectedTheme === 'light' ? primary : bgSecond,
            }
          ]}
        >
          <Ionicons name="sunny" size={20} color={selectedTheme === 'light' ? white : textMain} />
          <Text style={[textStyles.body, { color: selectedTheme === 'light' ? white : textMain }]}>Light</Text>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => handleThemeChange("system")}
          disabled={selectedTheme === "system"}
          style={[
            styles.themeButton,
            {
              backgroundColor: selectedTheme === 'system' ? primary : bgSecond,
              borderLeftWidth: 1,
              borderRightWidth: 1,
              borderColor: borderColor
            }
          ]}
        >
          <MaterialIcons name="settings-display" size={20} color={selectedTheme === 'system' ? white : textMain} />
          <Text style={[textStyles.body, { color: selectedTheme === 'system' ? white : textMain }]}>System</Text>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => handleThemeChange("dark")}
          disabled={selectedTheme === "dark"}
          style={[
            styles.themeButton,
            {
              backgroundColor: selectedTheme === 'dark' ? primary : bgSecond,
            }
          ]}
        >
          <Ionicons name="moon" size={20} color={selectedTheme === 'dark' ? white : textMain} />
          <Text style={[textStyles.body, { color: selectedTheme === 'dark' ? white : textMain }]}>Dark</Text>
        </TouchableOpacity>
      </View>
    </ShadowView>
  )
}

export default ThemeSelector

const styles = StyleSheet.create({
  themeSelect: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 8,
    borderWidth: 1,
  },
  themeButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 10,
    flexDirection: 'row',
    gap: 5
  }
})